<template>
  <div class="w-full p-4">
    <div class="flex flex-row justify-between items-center pb-2 border-b border-b-gray-400 mb-4">
      <h2 class="text-lg font-semibold">Expense Categories</h2>
      <button
        class="px-3 py-1.5 text-sm rounded-md bg-accent-primary text-white hover:bg-accent-secondary"
        @click="openNew"
      >+ New Category</button>
    </div>

    <div v-if="loading" class="text-center text-gray-500 py-6">Loading...</div>

    <!-- Mobile card list -->
    <div v-else class="md:hidden space-y-2">
      <div v-if="categories.length === 0" class="p-4 text-center text-gray-500 text-sm">
        No categories yet.
      </div>
      <div
        v-for="c in categories"
        :key="`m-${c.id}`"
        class="border border-gray-300 rounded-md bg-white p-3"
      >
        <div class="flex items-start justify-between gap-2">
          <div class="min-w-0">
            <div class="text-sm font-semibold break-words">{{ c.name }}</div>
            <div class="text-xs text-gray-500 break-words">{{ c.slug }}</div>
          </div>
          <div class="flex-shrink-0 text-xs">
            <span v-if="c.is_active" class="text-green-700">active</span>
            <span v-else class="text-gray-400">inactive</span>
          </div>
        </div>
        <div class="text-xs text-gray-500 mt-1">Order: {{ c.display_order }}</div>
        <div v-if="c.notes_hint" class="text-xs text-gray-700 mt-2 whitespace-pre-wrap">
          {{ c.notes_hint }}
        </div>
        <div class="mt-3 flex flex-wrap gap-2">
          <button
            class="text-xs px-2 py-1 border border-gray-400 rounded hover:bg-gray-100"
            @click="openEdit(c)"
          >Edit</button>
          <button
            class="text-xs px-2 py-1 border border-red-500 text-red-700 rounded hover:bg-red-50"
            @click="remove(c)"
          >Delete</button>
        </div>
      </div>
    </div>

    <!-- Desktop / tablet table -->
    <div v-if="!loading" class="hidden md:block border border-gray-400 rounded-md overflow-hidden">
      <table class="table-auto w-full text-sm">
        <thead class="bg-dark-primary text-white">
          <tr>
            <th class="text-left px-3 py-2">Order</th>
            <th class="text-left px-3 py-2">Name</th>
            <th class="text-left px-3 py-2">Slug</th>
            <th class="text-left px-3 py-2">Notes Hint</th>
            <th class="text-left px-3 py-2">Active</th>
            <th class="text-right px-3 py-2">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="c in categories" :key="c.id" class="border-t border-gray-200">
            <td class="px-3 py-2">{{ c.display_order }}</td>
            <td class="px-3 py-2">{{ c.name }}</td>
            <td class="px-3 py-2 text-xs text-gray-600">{{ c.slug }}</td>
            <td class="px-3 py-2">{{ c.notes_hint || '—' }}</td>
            <td class="px-3 py-2">
              <span v-if="c.is_active" class="text-green-700">active</span>
              <span v-else class="text-gray-400">inactive</span>
            </td>
            <td class="px-3 py-2 text-right">
              <button
                class="text-xs px-2 py-1 border border-gray-400 rounded hover:bg-gray-100"
                @click="openEdit(c)"
              >Edit</button>
              <button
                class="ml-2 text-xs px-2 py-1 border border-red-500 text-red-700 rounded hover:bg-red-50"
                @click="remove(c)"
              >Delete</button>
            </td>
          </tr>
          <tr v-if="categories.length === 0">
            <td colspan="6" class="px-3 py-4 text-center text-gray-500">No categories yet.</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="modalOpen" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50" @click.self="modalOpen = false">
      <div class="bg-white rounded-md shadow-lg p-5 w-[28rem]">
        <h3 class="text-base font-semibold mb-3">{{ editing ? 'Edit Category' : 'New Category' }}</h3>

        <label class="block text-sm mb-1">Name</label>
        <input v-model="form.name" type="text" class="w-full p-1.5 border border-gray-400 rounded-md mb-3" />

        <label class="block text-sm mb-1">Slug</label>
        <input v-model="form.slug" type="text" class="w-full p-1.5 border border-gray-400 rounded-md mb-3" />

        <label class="block text-sm mb-1">Display Order</label>
        <input v-model.number="form.display_order" type="number" class="w-full p-1.5 border border-gray-400 rounded-md mb-3" />

        <label class="block text-sm mb-1">Notes Hint (shown below the Notes field when this category is selected)</label>
        <textarea v-model="form.notes_hint" rows="2" class="w-full p-1.5 border border-gray-400 rounded-md mb-3"></textarea>

        <label class="flex items-center gap-2 text-sm mb-3">
          <input v-model="form.is_active" type="checkbox" />
          Active
        </label>

        <div v-if="modalError" class="text-sm text-red-700 mb-2">{{ modalError }}</div>

        <div class="flex justify-end gap-2">
          <button class="text-sm px-3 py-1.5 border border-gray-400 rounded hover:bg-gray-100" @click="modalOpen = false">Cancel</button>
          <button class="text-sm px-3 py-1.5 rounded bg-accent-primary text-white hover:bg-accent-secondary disabled:opacity-50" :disabled="saving" @click="save">{{ saving ? 'Saving...' : 'Save' }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default { name: "expense-categories-admin" };
</script>

<script setup>
import { ref, reactive, onMounted } from "vue";
import { useStore } from "vuex";
import { useToast } from "vue-toast-notification";
import { getExpenseCategories } from "@/services/ExpenseCategory.js";
import { createCategory, updateCategory, deleteCategory } from "@/services/ExpenseAdmin.js";

const store = useStore();
const $toast = useToast();

const loading = ref(true);
const categories = ref([]);
const modalOpen = ref(false);
const editing = ref(null);
const saving = ref(false);
const modalError = ref("");

const form = reactive({ name: "", slug: "", display_order: 0, notes_hint: "", is_active: true });

async function load() {
  loading.value = true;
  const token = store.state.auth.token;
  try {
    const data = await getExpenseCategories(token);
    categories.value = Array.isArray(data) ? data : (data.results || []);
  } catch (error) {
    $toast.open({ message: "Failed to load categories", type: "error", duration: 3000 });
  }
  loading.value = false;
}

function openNew() {
  editing.value = null;
  form.name = "";
  form.slug = "";
  form.display_order = (categories.value.length + 1) * 10;
  form.notes_hint = "";
  form.is_active = true;
  modalError.value = "";
  modalOpen.value = true;
}

function openEdit(c) {
  editing.value = c;
  form.name = c.name;
  form.slug = c.slug;
  form.display_order = c.display_order;
  form.notes_hint = c.notes_hint || "";
  form.is_active = c.is_active;
  modalError.value = "";
  modalOpen.value = true;
}

async function save() {
  saving.value = true;
  modalError.value = "";
  const token = store.state.auth.token;
  const payload = { ...form };
  try {
    if (editing.value) {
      await updateCategory(token, editing.value.id, payload);
      $toast.open({ message: "Updated", type: "success", duration: 2000 });
    } else {
      await createCategory(token, payload);
      $toast.open({ message: "Created", type: "success", duration: 2000 });
    }
    modalOpen.value = false;
    await load();
  } catch (error) {
    modalError.value = (error.response && error.response.data && JSON.stringify(error.response.data))
      || error.message || "Failed";
  }
  saving.value = false;
}

async function remove(c) {
  if (!window.confirm(`Delete category "${c.name}"? Expenses already using it will retain the category.`)) return;
  const token = store.state.auth.token;
  try {
    await deleteCategory(token, c.id);
    $toast.open({ message: "Deleted", type: "success", duration: 2000 });
    await load();
  } catch (error) {
    $toast.open({
      message: (error.response && error.response.data && JSON.stringify(error.response.data))
        || "Failed to delete (category may be in use)",
      type: "error", duration: 4000
    });
  }
}

onMounted(load);
</script>
